import { once } from 'lodash';
import { SynchronizedHistory } from 'mobx-react-router';
import { createContext, Dispatch, SetStateAction, useContext } from 'react';
import { GraphQlRequestService } from '../../api/graphql-request-service';
import { AuthenticationService } from '../../auth/auth-service';
import { ForgotPasswordService } from '../../auth/forgot-password-service';
import { EnvironmentService } from '../../environment/environment-service';
import { IntercomStore } from '../../intercom/intercom-store';
import { LanguageStore } from '../../localization/language-store';
import {
  BaseAuthedTracker,
  BaseTracker,
} from '../analytics/trackers/baseTracker';
import { DashboardTracker } from '../analytics/trackers/dashboardTracker';
import { FilterTracker } from '../analytics/trackers/filterTracker';
import { SidebarTracker } from '../analytics/trackers/sidebarTracker';
import { TimeSliderTracker } from '../analytics/trackers/timeSliderTracker';
import { UserTracker } from '../analytics/trackers/userTracker';
import { AliasService } from '../api/alias/service';
import { ApiAlias } from '../api/alias/types';
import {
  EffectiveRoleViewAuthorizationService,
  ExecutorRoleViewAuthorizationService,
  OriginalRoleViewAuthorizationService,
} from '../api/authorization/service';
import { ChappiService } from '../api/chappi/service';
import { DomainViewDashboardService } from '../api/dashboards/domain-view-dashboard-service';
import { FullViewDashboardService } from '../api/dashboards/full-view-dashboard-service';
import { UserViewDashboardService } from '../api/dashboards/user-view-dashboard-service';
import { DataService } from '../api/data/service';
import { DataUploadService } from '../api/dataupload/service';
import { DomainDependenciesService } from '../api/domaindependencies/services';
import { DomainDependencies } from '../api/domaindependencies/types';
import { DomainPreferencesService } from '../api/domainpreferences/service';
import { DomainSettingsService } from '../api/domainsettings/service';
import { EmployeeService } from '../api/employee/service';
import { FeatureAccessService } from '../api/featureaccess/service';
import { FeatureFlagsService } from '../api/featureflags/service';
import { FieldsService } from '../api/fields/service';
import {
  FrontendStartupCurrentUser,
  FrontendStartupRoleDetails,
} from '../api/frontendStartup/types';
import { IpAddressService } from '../api/ipaddress/service';
import { LanguageObject } from '../api/language/types';
import {
  DefaultLocalStorageService,
  JsonLocalStorageService,
} from '../api/local-storage-service';
import { MetricsQueryService } from '../api/metrics/metrics-query-service';
import { MetricsService } from '../api/metrics/metrics-service';
import { MetricDetails, MetricTree } from '../api/metrics/types';
import { NotificationService } from '../api/notifications/service';
import { PermissionService } from '../api/permission/service';
import { AssignedPermissions, Permission } from '../api/permission/types';
import { BackendRoleService } from '../api/role/service';
import { AuthenticatedGraphQLBackendService } from '../api/service';
import {
  AllowedDomain,
  DomainPreferences,
  DomainSettings,
  LatestDomainSettings,
  Role,
  RoleId,
  VersionId,
} from '../api/types';
import {
  CohortMetricId,
  EmployeeCohortMetricId,
  RegularMetricId,
} from '../api/types-graphql';
import { FirebaseUser } from '../api/user/types';
import { UserRolePreferencesService } from '../api/userrolepreferences/service';
import { UserRolePreferencesSettings } from '../api/userrolepreferences/types';
import { Dashboard } from '../common/components/dashboards/types';
import { AuthedErrorTracker } from '../common/components/errors/trackers/AuthedErrorTracker';
import { DomainScopedErrorTracker } from '../common/components/errors/trackers/DomainScopedErrorTracker';
import { UnauthErrorTracker } from '../common/components/errors/trackers/UnauthErrorTracker';
import {
  Filter,
  FilterTypes,
} from '../common/components/filter/filterbar/types';
import { FilterSection } from '../common/components/filter/types';
import {
  TimePointSelectorHandle,
  TimeSliderHandle,
} from '../common/components/timeslider/types';
import { Domains, Languages } from '../constants';
import { AllUsedFields, DataFieldWithDataType, DataTypes } from '../types';
import { LegacyDomainScopedStore, LegacyStore } from './legacy-types';
import { Mixpanel } from './mixpanel';
import {
  GlobalDisplayHandle,
  GlobalFilterHandle,
  GlobalResetHandle,
  GlobalSegmentHandle,
} from './types';

export interface FrontendStartupWithSimulation {
  roleDetails: FrontendStartupRoleDetails;
  originalRoleDetails: FrontendStartupRoleDetails;
  dashboards: Dashboard[] | null;
  currentUser: FrontendStartupCurrentUser;
}

export const FrontendStartupContext =
  createContext<FrontendStartupWithSimulation | null>(null);

export const useFrontendStartupContext = () => {
  const context = useContext(FrontendStartupContext);
  if (!context) {
    throw new Error(
      'useFrontendStartupContext must be used within a FrontendStartupContext'
    );
  }
  return context;
};

export const CurrentUserContext =
  createContext<FrontendStartupCurrentUser | null>(null);

export const useCurrentUserContext = () => {
  const context = useContext(CurrentUserContext);
  if (!context) {
    throw new Error(
      'useCurrentUserContext must be used within a CurrentUserContext'
    );
  }
  return context;
};

export const ExecutorRolesContext = createContext<Role[] | null>(null);

export const useExecutorRolesContext = () => {
  const context = useContext(ExecutorRolesContext);
  if (!context) {
    throw new Error(
      'useExecutorRolesContext must be used within a ExecutorRolesContext'
    );
  }
  return context;
};

export const FeatureFlagsServiceContext =
  createContext<FeatureFlagsService | null>(null);

export const useFeatureFlagsServiceContext = () => {
  const context = useContext(FeatureFlagsServiceContext);
  if (!context) {
    throw new Error(
      'useFeatureFlagsServiceContext must be used within a FeatureFlagsServiceContext'
    );
  }
  return context;
};

export const FeatureAccessServiceContext =
  createContext<FeatureAccessService | null>(null);

export const useFeatureAccessServiceContext = () => {
  const context = useContext(FeatureAccessServiceContext);
  if (!context) {
    throw new Error(
      'useFeatureAccessServiceContext must be used within a FeatureAccessServiceContext'
    );
  }
  return context;
};

export const DataUploadServiceContext = createContext<DataUploadService | null>(
  null
);

export const useDataUploadServiceContext = () => {
  const context = useContext(DataUploadServiceContext);
  if (!context) {
    throw new Error(
      'useDataUploadServiceContext must be used within a DataUploadServiceContext'
    );
  }
  return context;
};

export const DomainDependenciesServiceContext =
  createContext<DomainDependenciesService | null>(null);

export const useDomainDependenciesServiceContext = () => {
  const context = useContext(DomainDependenciesServiceContext);
  if (!context) {
    throw new Error(
      'useDomainDependenciesServiceContext must be used within a DomainDependenciesServiceContext'
    );
  }
  return context;
};

export const PermissionServiceContext = createContext<PermissionService | null>(
  null
);

export const usePermissionServiceContext = () => {
  const context = useContext(PermissionServiceContext);
  if (!context) {
    throw new Error(
      'usePermissionServiceContext must be used within a PermissionServiceContext'
    );
  }
  return context;
};

export const EffectivePermissionsContext =
  createContext<AssignedPermissions<Permission> | null>(null);

export const useEffectivePermissionsContext = () => {
  const context = useContext(EffectivePermissionsContext);
  if (!context) {
    throw new Error(
      'useEffectivePermissionsContext must be used within a EffectivePermissionsContext'
    );
  }
  return context;
};

export const LatestSettingsContext = createContext<DomainSettings | null>(null);

export const useLatestSettingsContext = () => {
  const context = useContext(LatestSettingsContext);
  if (!context) {
    throw new Error(
      'useLatestSettingsContext must be used within a LatestSettingsContext'
    );
  }
  return context;
};

export const LatestSettingsUpdaterContext = createContext<
  ((d: DomainSettings) => void) | null
>(null);

export const useLatestSettingsUpdaterContext = () => {
  const context = useContext(LatestSettingsUpdaterContext);
  if (!context) {
    throw new Error(
      'useLatestSettingsUpdaterContext must be used within a LatestSettingsUpdaterContext'
    );
  }
  return context;
};

export const LatestPreferencesUpdaterContext = createContext<
  ((d: DomainPreferences) => void) | null
>(null);

export const useLatestPreferencesUpdaterContext = () => {
  const context = useContext(LatestPreferencesUpdaterContext);
  if (!context) {
    throw new Error(
      'useLatestPreferencesUpdaterContext must be used within a LatestPreferencesUpdaterContext'
    );
  }
  return context;
};

export const LatestPreferencesContext = createContext<DomainPreferences | null>(
  null
);

export const useLatestPreferencesContext = () => {
  const context = useContext(LatestPreferencesContext);
  if (!context) {
    throw new Error(
      'useLatestPreferencesContext must be used within a LatestPreferencesContext'
    );
  }
  return context;
};

export const DomainDependenciesContext =
  createContext<DomainDependencies | null>(null);

export const useDomainDependenciesContext = () => {
  const context = useContext(DomainDependenciesContext);
  if (!context) {
    throw new Error(
      'useDomainDependenciesContext must be used within a DomainDependenciesContext'
    );
  }
  return context;
};

export const LegacyStoresContext = createContext<LegacyStore | null>(null);

export const useLegacyStoresContext = () => {
  const context = useContext(LegacyStoresContext);
  if (!context) {
    throw new Error(
      'useLegacyStoresContext must be used within a LegacyStoresContext'
    );
  }
  return context;
};

export const UserRoleIdContext = createContext<RoleId | null>(null);

export const useUserRoleIdContext = () => {
  const context = useContext(UserRoleIdContext);
  if (!context) {
    throw new Error(
      'useUserRoleIdContext must be used within a UserRoleIdContext'
    );
  }
  return context;
};

export const AvailableDashboardsContext = createContext<Dashboard[] | null>(
  null
);

export const useAvailableDashboardsContext = () => {
  const context = useContext(AvailableDashboardsContext);
  if (!context) {
    throw new Error(
      'useAvailableDashboardsContext must be used within a AvailableDashboardsContext'
    );
  }
  return context;
};

export const AuthenticationServiceContext =
  createContext<AuthenticationService | null>(null);

export const useAuthenticationServiceContext = () => {
  const context = useContext(AuthenticationServiceContext);
  if (!context) {
    throw new Error(
      'useAuthenticationServiceContext must be used within a AuthenticationServiceContext'
    );
  }
  return context;
};

export const GraphQlRequestServiceContext =
  createContext<GraphQlRequestService | null>(null);

export const useGraphQlRequestServiceContext = () => {
  const context = useContext(GraphQlRequestServiceContext);
  if (!context) {
    throw new Error(
      'useGraphQlRequestServiceContext must be used within a GraphQlRequestServiceContext'
    );
  }
  return context;
};

export const SimulatedRoleContext = createContext<Role | null | undefined>(
  undefined
);

export const useSimulatedRoleContext = () => {
  const context = useContext(SimulatedRoleContext);
  if (context === undefined) {
    throw new Error(
      'useSimulatedRoleContext must be used within a SimulatedRoleContextProvider'
    );
  }
  return context;
};

export const SimulatedRoleSetterContext = createContext<
  ((d: Role | null) => void) | null
>(null);

export const useSimulatedRoleSetterContext = () => {
  const context = useContext(SimulatedRoleSetterContext);
  if (!context) {
    throw new Error(
      'useSimulatedRoleSetterContext must be used within a SimulatedRoleSetterContextProvider'
    );
  }
  return context;
};

export const ExecutorRoleContext = createContext<Role | null>(null);

export const useExecutorRoleContext = () => {
  const context = useContext(ExecutorRoleContext);
  if (!context) {
    throw new Error(
      'useExecutorRoleContext must be used within a ExecutorRoleContextProvider'
    );
  }
  return context;
};

export const ExecutorRoleSetterContext = createContext<
  ((d: Role) => void) | null
>(null);

export const useExecutorRoleSetterContext = () => {
  const context = useContext(ExecutorRoleSetterContext);
  if (!context) {
    throw new Error(
      'useExecutorRoleSetterContext must be used within a ExecutorRoleSetterContextProvider'
    );
  }
  return context;
};

export const ExecutorRolePermissionsContext =
  createContext<AssignedPermissions<Permission> | null>(null);

export const useExecutorRolePermissionsContext = () => {
  const context = useContext(ExecutorRolePermissionsContext);
  if (!context) {
    throw new Error(
      'useExecutorRolePermissionsContext must be used within a ExecutorRolePermissionsContextProvider'
    );
  }
  return context;
};

export const OldestStartDateContext = createContext<string | null>(null);

export const useOldestStartDateContext = () => {
  const context = useContext(OldestStartDateContext);
  if (!context) {
    throw new Error(
      'useOldestStartDateContext must be used within a OldestStartDateContextProvider'
    );
  }
  return context;
};

export const AllUsedFieldsContext = createContext<AllUsedFields | null>(null);

export const useAllUsedFieldsContext = () => {
  const context = useContext(AllUsedFieldsContext);
  if (!context) {
    throw new Error(
      'useAllUsedFieldsContext must be used within a AllUsedFieldsContextProvider'
    );
  }
  return context;
};

export const FieldsServiceContext = createContext<FieldsService | null>(null);

export const useFieldsServiceContext = () => {
  const context = useContext(FieldsServiceContext);
  if (!context) {
    throw new Error(
      'useFieldsServiceContext must be used within a FieldsServiceContextProvider'
    );
  }
  return context;
};

export const AliasesContext = createContext<ApiAlias[] | null>(null);

export const useAliasesContext = () => {
  const context = useContext(AliasesContext);
  if (!context) {
    throw new Error(
      'useAliasesContext must be used within a AliasesContextProvider'
    );
  }
  return context;
};

export const AliasesUpdaterContext = createContext<Dispatch<
  SetStateAction<ApiAlias[] | null>
> | null>(null);

export const useAliasesUpdaterContext = () => {
  const context = useContext(AliasesUpdaterContext);
  if (!context) {
    throw new Error(
      'useAliasesUpdaterContext must be used within a AliasesUpdaterContextProvider'
    );
  }
  return context;
};

export const UserRolesContext = createContext<Role[] | null>(null);

export const useUserRolesContext = () => {
  const context = useContext(UserRolesContext);
  if (!context) {
    throw new Error(
      'useUserRolesContext must be used within a UserRolesContextProvider'
    );
  }
  return context;
};

export const BackendRoleServiceContext =
  createContext<BackendRoleService | null>(null);

export const useBackendRoleServiceContext = () => {
  const context = useContext(BackendRoleServiceContext);
  if (!context) {
    throw new Error(
      'useBackendRoleServiceContext must be used within a BackendRoleServiceContextProvider'
    );
  }
  return context;
};

export const EffectiveRoleContext = createContext<RoleId | null>(null);

export const useEffectiveRoleContext = () => {
  const context = useContext(EffectiveRoleContext);
  if (!context) {
    throw new Error(
      'useEffectiveRoleContext must be used within a EffectiveRoleContextProvider'
    );
  }
  return context;
};

export const LatestVersionsContext = createContext<Partial<
  Record<DataTypes, VersionId>
> | null>(null);

export const useLatestVersionsContext = () => {
  const context = useContext(LatestVersionsContext);
  if (!context) {
    throw new Error(
      'useLatestVersionsContext must be used within a LatestVersionsContextProvider'
    );
  }
  return context;
};

export const LatestEmployeeVersionContext = createContext<VersionId | null>(
  null
);

export const useLatestEmployeeVersionContext = () => {
  const context = useContext(LatestEmployeeVersionContext);
  if (!context) {
    throw new Error(
      'useLatestEmployeeVersionContext must be used within a LatestEmployeeVersionContextProvider'
    );
  }
  return context;
};

export const PermittedFiltersContext = createContext<
  DataFieldWithDataType[] | null
>(null);

export const usePermittedFiltersContext = () => {
  const context = useContext(PermittedFiltersContext);
  if (!context) {
    throw new Error(
      'usePermittedFiltersContext must be used within a PermittedFiltersContextProvider'
    );
  }
  return context;
};

export const AllowedDomainsContext = createContext<AllowedDomain[] | null>(
  null
);

export const useAllowedDomainsContext = () => {
  const context = useContext(AllowedDomainsContext);
  if (!context) {
    throw new Error(
      'useAllowedDomainsContext must be used within a AllowedDomainsContextProvider'
    );
  }
  return context;
};

export const AllowedDomainContext = createContext<AllowedDomain | null>(null);

export const useAllowedDomainContext = () => {
  const context = useContext(AllowedDomainContext);
  if (!context) {
    throw new Error(
      'useAllowedDomainContext must be used within a AllowedDomainContextProvider'
    );
  }
  return context;
};

export const AllLatestDomainSettingsContext = createContext<
  LatestDomainSettings[] | null
>(null);

export const useAllLatestDomainSettingsContext = () => {
  const context = useContext(AllLatestDomainSettingsContext);
  if (!context) {
    throw new Error(
      'useAllLatestDomainSettingsContext must be used within a AllLatestDomainSettingsContextProvider'
    );
  }
  return context;
};

export const DomainContext = createContext<Domains | null>(null);

export const useDomainContext = () => {
  const context = useContext(DomainContext);
  if (!context) {
    throw new Error(
      'useDomainContext must be used within a DomainContextProvider'
    );
  }
  return context;
};

export const DomainSetterContext = createContext<((d: Domains) => void) | null>(
  null
);

export const useDomainSetterContext = () => {
  const context = useContext(DomainSetterContext);
  if (!context) {
    throw new Error(
      'useDomainSetterContext must be used within a DomainSetterContextProvider'
    );
  }
  return context;
};

export const FullViewDashboardServiceContext =
  createContext<FullViewDashboardService | null>(null);

export const useFullViewDashboardServiceContext = () => {
  const context = useContext(FullViewDashboardServiceContext);
  if (!context) {
    throw new Error(
      'useFullViewDashboardServiceContext must be used within a FullViewDashboardServiceContextProvider'
    );
  }
  return context;
};

export const AuthUserContext = createContext<FirebaseUser | null>(null);

export const useAuthUserContext = () => {
  const context = useContext(AuthUserContext);
  if (!context) {
    throw new Error(
      'useAuthUserContext must be used within a AuthUserContextProvider'
    );
  }
  return context;
};

export const AuthUserSetterContext = createContext<
  ((user: FirebaseUser) => void) | null
>(null);

export const useAuthUserSetterContext = () => {
  const context = useContext(AuthUserSetterContext);
  if (!context) {
    throw new Error(
      'useAuthUserSetterContext must be used within a AuthUserSetterContextProvider'
    );
  }
  return context;
};

export const NullableDomainContext = createContext<Domains | null>(null);

export const useNullableDomainContext = () => {
  const context = useContext(NullableDomainContext);
  return context;
};

export const LocalStorageDomainServiceContext =
  createContext<DefaultLocalStorageService<Domains> | null>(null);

export const useLocalStorageDomainServiceContext = () => {
  const context = useContext(LocalStorageDomainServiceContext);
  if (!context) {
    throw new Error(
      'useLocalStorageDomainServiceContext must be used within a LocalStorageDomainServiceContextProvider'
    );
  }
  return context;
};

export const DomainFromLocalStorageContext = createContext<Domains | null>(
  null
);

export const useDomainFromLocalStorageContext = () => {
  const context = useContext(DomainFromLocalStorageContext);
  return context;
};

export const CurrentUserFromLocalStorageContext =
  createContext<FirebaseUser | null>(null);

export const useCurrentUserFromLocalStorageContext = () => {
  const context = useContext(CurrentUserFromLocalStorageContext);
  return context;
};

export const ExecutorRoleFromLocalStorageContext = createContext<Role | null>(
  null
);

export const useExecutorRoleFromLocalStorageContext = () => {
  const context = useContext(ExecutorRoleFromLocalStorageContext);
  return context;
};

export const EnvironmentServiceContext =
  createContext<EnvironmentService | null>(null);

export const useEnvironmentServiceContext = () => {
  const context = useContext(EnvironmentServiceContext);
  if (!context) {
    throw new Error(
      'useEnvironmentServiceContext must be used within a EnvironmentServiceContextProvider'
    );
  }
  return context;
};

export const UnauthErrorTrackerContext =
  createContext<UnauthErrorTracker | null>(null);

export const useUnauthErrorTrackerContext = () => {
  const context = useContext(UnauthErrorTrackerContext);
  if (!context) {
    throw new Error(
      'useUnauthErrorTrackerContext must be used within a UnauthErrorTrackerContextProvider'
    );
  }
  return context;
};

export const AuthedErrorTrackerContext =
  createContext<AuthedErrorTracker | null>(null);

export const useAuthedErrorTrackerContext = () => {
  const context = useContext(AuthedErrorTrackerContext);
  if (!context) {
    throw new Error(
      'useAuthedErrorTrackerContext must be used within a AuthedErrorTrackerContextProvider'
    );
  }
  return context;
};

// @Alex, wondering if we can simplify this boilerplate
export const DomainViewDashboardServiceContext =
  createContext<DomainViewDashboardService | null>(null);

export const useDomainViewDashboardServiceContext = () => {
  const context = useContext(DomainViewDashboardServiceContext);
  if (!context) {
    throw new Error(
      'useDomainViewDashboardServiceContext must be used within a DomainViewDashboardServiceContextProvider'
    );
  }
  return context;
};

export const UserViewDashboardServiceContext =
  createContext<UserViewDashboardService | null>(null);

export const useUserViewDashboardServiceContext = () => {
  const context = useContext(UserViewDashboardServiceContext);
  if (!context) {
    throw new Error(
      'useUserViewDashboardServiceContext must be used within a UserViewDashboardServiceContextProvider'
    );
  }
  return context;
};

export const LocaleContext = createContext<{
  selected: Languages;
  object: LanguageObject;
} | null>(null);

export const useGlobalLocaleContext = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error(
      'useGlobalLocaleContext must be used within a LocaleContextProvider'
    );
  }
  return context;
};

export const LanguageStoreContext = createContext<LanguageStore | null>(null);

export const useLanguageStoreContext = () => {
  const context = useContext(LanguageStoreContext);
  if (!context) {
    throw new Error(
      'useLanguageStoreContext must be used within a LanguageStoreContextProvider'
    );
  }
  return context;
};

export const LocalStorageExecutorRoleServiceContext =
  createContext<JsonLocalStorageService<Role> | null>(null);

export const useLocalStorageExecutorRoleServiceContext = () => {
  const context = useContext(LocalStorageExecutorRoleServiceContext);
  if (!context) {
    throw new Error(
      'useLocalStorageExecutorRoleServiceContext must be used within a LocalStorageExecutorRoleServiceContextProvider'
    );
  }
  return context;
};

export const IntercomStoreContext = createContext<IntercomStore | null>(null);

export const useIntercomStoreContext = () => {
  const context = useContext(IntercomStoreContext);
  if (!context) {
    throw new Error(
      'useIntercomStoreContext must be used within a IntercomStoreContextProvider'
    );
  }
  return context;
};

export const ResetContext = createContext<GlobalResetHandle | null>(null);

export const useGlobalResetContext = () => {
  const context = useContext(ResetContext);
  if (!context) {
    throw new Error(
      'useGlobalResetContext must be used within a ResetContextProvider'
    );
  }
  return context;
};

export const FilterContext = createContext<GlobalFilterHandle | null>(null);

export const useGlobalFilterContext = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error(
      'useGlobalFilterContext must be used within a FilterContextProvider'
    );
  }
  return context;
};

export const FilterRecruitmentContext =
  createContext<GlobalFilterHandle | null>(null);

export const useGlobalFilterRecruitmentContext = () => {
  const context = useContext(FilterRecruitmentContext);
  if (!context) {
    throw new Error(
      'useGlobalFilterRecruitmentContext must be used within a FilterRecruitmentContextProvider'
    );
  }
  return context;
};

export const FilterMovementContext = createContext<GlobalFilterHandle | null>(
  null
);

export const useGlobalFilterMovementContext = () => {
  const context = useContext(FilterMovementContext);
  if (!context) {
    throw new Error(
      'useGlobalFilterMovementContext must be used within a FilterMovementContextProvider'
    );
  }
  return context;
};

export const TimeSliderContext = createContext<TimeSliderHandle | null>(null);

export const useGlobalTimeSliderContext = () => {
  const context = useContext(TimeSliderContext);
  if (!context) {
    throw new Error(
      'useGlobalTimeSliderContext must be used within a TimeSliderContextProvider'
    );
  }
  return context;
};

export const TimePointSelectorContext =
  createContext<TimePointSelectorHandle | null>(null);

export const useTimePointSelectorContext = () => {
  const context = useContext(TimePointSelectorContext);
  if (!context) {
    throw new Error(
      'useTimePointSelectorContext must be used within a TimePointSelectorContextProvider'
    );
  }
  return context;
};

export const MetricDetailsMapContext = createContext<Record<
  RegularMetricId | CohortMetricId | EmployeeCohortMetricId,
  MetricDetails
> | null>(null);

export const useMetricDetailsMapContext = () => {
  const context = useContext(MetricDetailsMapContext);
  if (!context) {
    throw new Error(
      'useMetricDetailsMapContext must be used within a MetricDetailsMapProvider'
    );
  }
  return context;
};

export const MetricTreeContext = createContext<MetricTree | null>(null);

export const useMetricTreeContext = () => {
  const context = useContext(MetricTreeContext);
  if (!context) {
    throw new Error(
      'useMetricTreeContext must be used within a MetricTreeContext'
    );
  }
  return context;
};

export const SegmentationLevel1Context =
  createContext<GlobalSegmentHandle | null>(null);

export const useGlobalSegmentationLevel1Context = () => {
  const context = useContext(SegmentationLevel1Context);
  if (!context) {
    throw new Error(
      'useGlobalSegmentationLevel1Context must be used within a SegmentationLevel1ContextProvider'
    );
  }
  return context;
};

export const SegmentationLevel2Context =
  createContext<GlobalSegmentHandle | null>(null);

export const useGlobalSegmentationLevel2Context = () => {
  const context = useContext(SegmentationLevel2Context);
  if (!context) {
    throw new Error(
      'useGlobalSegmentationLevel2Context must be used within a SegmentationLevel2ContextProvider'
    );
  }
  return context;
};

export const SegmentationLevel1RecruitmentContext =
  createContext<GlobalSegmentHandle | null>(null);

export const useGlobalSegmentationLevel1RecruitmentContext = () => {
  const context = useContext(SegmentationLevel1RecruitmentContext);
  if (!context) {
    throw new Error(
      'useGlobalSegmentationLevel1RecruitmentContext must be used within a SegmentationLevel1RecruitmentContextProvider'
    );
  }
  return context;
};

export const SegmentationLevel2RecruitmentContext =
  createContext<GlobalSegmentHandle | null>(null);

export const useGlobalSegmentationLevel2RecruitmentContext = () => {
  const context = useContext(SegmentationLevel2RecruitmentContext);
  if (!context) {
    throw new Error(
      'useGlobalSegmentationLevel2RecruitmentContext must be used within a SegmentationLevel2RecruitmentContextProvider'
    );
  }
  return context;
};

export const DisplayContext = createContext<GlobalDisplayHandle | null>(null);

export const useDisplayGlobalContext = () => {
  const context = useContext(DisplayContext);
  if (!context) {
    throw new Error(
      'useDisplayContext must be used within a DisplayContextProvider'
    );
  }
  return context;
};

export const UserRolePreferencesSettingsContext =
  createContext<UserRolePreferencesSettings<Filter> | null>(null);

export const useUserRolePreferencesSettingsContext = () => {
  const context = useContext(UserRolePreferencesSettingsContext);
  return context;
};

export const UserRolePreferencesServiceContext = once(<
  T extends FilterTypes,
>() => createContext<UserRolePreferencesService<T> | null>(null));

export const useUserRolePreferencesServiceContext = <
  T extends FilterTypes,
>() => {
  const context = useContext(UserRolePreferencesServiceContext<T>());
  if (!context) {
    throw new Error(
      'useUserRolePreferencesService must be used within a UserRolePreferencesServiceContextProvider'
    );
  }
  return context;
};

export const AliasServiceContext = createContext<AliasService | null>(null);

export const useAliasServiceContext = () => {
  const context = useContext(AliasServiceContext);
  if (!context) {
    throw new Error(
      'useAliasService must be used within a AliasServiceContextProvider'
    );
  }
  return context;
};

export const MetricsServiceContext = createContext<MetricsService | null>(null);

export const useMetricsServiceContext = () => {
  const context = useContext(MetricsServiceContext);
  if (!context) {
    throw new Error(
      'useMetricServiceContext must be used within a MetricsServiceContext'
    );
  }
  return context;
};

export const MetricsQueryServiceContext =
  createContext<MetricsQueryService | null>(null);

export const useMetricsQueryServiceContext = () => {
  const context = useContext(MetricsQueryServiceContext);
  if (!context) {
    throw new Error(
      'useMetricQueryServiceContext must be used within a MetricsQueryServiceContext'
    );
  }
  return context;
};

export const ExecutorRoleViewAuthorizationServiceContext =
  createContext<ExecutorRoleViewAuthorizationService | null>(null);

export const useExecutorRoleViewAuthorizationServiceContext = () => {
  const context = useContext(ExecutorRoleViewAuthorizationServiceContext);
  if (!context) {
    throw new Error(
      'useExecutorRoleViewAuthorizationServiceContext must be used within a ExecutorRoleViewAuthorizationContextProvider'
    );
  }
  return context;
};

export const EffectiveRoleViewAuthorizationServiceContext =
  createContext<EffectiveRoleViewAuthorizationService | null>(null);

export const useEffectiveRoleViewAuthorizationServiceContext = () => {
  const context = useContext(EffectiveRoleViewAuthorizationServiceContext);
  if (!context) {
    throw new Error(
      'useEffectiveRoleViewAuthorizationServiceContext must be used within a EffectiveRoleViewAuthorizationContextProvider'
    );
  }
  return context;
};

export const OriginalRoleViewAuthorizationServiceContext =
  createContext<OriginalRoleViewAuthorizationService | null>(null);

export const useOriginalRoleViewAuthorizationServiceContext = () => {
  const context = useContext(OriginalRoleViewAuthorizationServiceContext);
  if (!context) {
    throw new Error(
      'useOriginalRoleViewAuthorizationServiceContext must be used within a OriginalRoleViewAuthorizationServiceContextProvider'
    );
  }
  return context;
};

export const BackendServiceContext =
  createContext<AuthenticatedGraphQLBackendService | null>(null);

export const useBackendServiceContext = () => {
  const context = useContext(BackendServiceContext);
  if (!context) {
    throw new Error(
      'useBackendServiceContext must be used within a BackendContextProvider'
    );
  }
  return context;
};

export const EmployeeServiceContext = createContext<EmployeeService | null>(
  null
);

export const useEmployeeServiceContext = () => {
  const context = useContext(EmployeeServiceContext);
  if (!context) {
    throw new Error(
      'useEmployeeServiceContext must be used within a EmployeeContextProvider'
    );
  }
  return context;
};

export const DataServiceContext = createContext<DataService | null>(null);

export const useDataServiceContext = () => {
  const context = useContext(DataServiceContext);
  if (!context) {
    throw new Error(
      'useDataServiceContext must be used within a DataServiceContextProvider'
    );
  }
  return context;
};

export const BaseTrackerContext = createContext<BaseTracker | null>(null);

export const useBaseTrackerContext = () => {
  const context = useContext(BaseTrackerContext);
  if (!context) {
    throw new Error(
      'useBaseTrackerContext must be used within a BaseTrackerContextProvider'
    );
  }
  return context;
};

export const BaseAuthedTrackerContext = createContext<BaseAuthedTracker | null>(
  null
);

export const useBaseAuthedTrackerContext = () => {
  const context = useContext(BaseAuthedTrackerContext);
  if (!context) {
    throw new Error(
      'useBaseAuthedTrackerContext must be used within a BaseAuthedTrackerContextProvider'
    );
  }
  return context;
};

export const UserTrackerContext = createContext<UserTracker | null>(null);

export const useUserTrackerContext = () => {
  const context = useContext(UserTrackerContext);
  if (!context) {
    throw new Error(
      'useUserTrackerContext must be used within a UserTrackerContextProvider'
    );
  }
  return context;
};

// TODO: Not sure that we want to keep this in v2 since we are not using mobx
export const ReactRouterHistoryContext =
  createContext<SynchronizedHistory | null>(null);

export const useReactRouterHistoryContext = () => {
  const context = useContext(ReactRouterHistoryContext);
  if (!context) {
    throw new Error(
      'useReactRouterHistoryContext must be used within a ReactRouterHistoryContextProvider'
    );
  }
  return context;
};

export const DomainSettingsServiceContext =
  createContext<DomainSettingsService | null>(null);

export const useDomainSettingsServiceContext = () => {
  const context = useContext(DomainSettingsServiceContext);
  if (!context) {
    throw new Error(
      'useDomainSettingsServiceContext must be used within a DomainSettingsServiceContextProvider'
    );
  }
  return context;
};

export const DomainPreferencesServiceContext =
  createContext<DomainPreferencesService | null>(null);

export const useDomainPreferencesServiceContext = () => {
  const context = useContext(DomainPreferencesServiceContext);
  if (!context) {
    throw new Error(
      'useDomainPreferencesServiceContext must be used within a DomainPreferencesServiceContextProvider'
    );
  }
  return context;
};

export const DomainScopedErrorTrackerContext =
  createContext<DomainScopedErrorTracker | null>(null);

export const useDomainScopedErrorTrackerContext = () => {
  const context = useContext(DomainScopedErrorTrackerContext);
  if (!context) {
    throw new Error(
      'useDomainScopedErrorTrackerContext must be used within a DomainScopedErrorTrackerContextProvider'
    );
  }
  return context;
};

export const FilterSectionsContext = createContext<FilterSection[] | null>(
  null
);

export const useFilterSectionsContext = () => {
  const context = useContext(FilterSectionsContext);
  if (!context) {
    throw new Error(
      'useFilterSectionsContext must be used within a FilterSectionsContextProvider'
    );
  }
  return context;
};

export const SegmentationSectionsContext = createContext<
  FilterSection[] | null
>(null);

export const useSegmentationSectionsContext = () => {
  const context = useContext(SegmentationSectionsContext);
  if (!context) {
    throw new Error(
      'useSegmentationSectionsContext must be used within a SegmentationSectionsContextProvider'
    );
  }
  return context;
};

export const ForgotPasswordServiceContext =
  createContext<ForgotPasswordService | null>(null);

export const useForgotPasswordServiceContext = () => {
  const context = useContext(ForgotPasswordServiceContext);
  if (!context) {
    throw new Error(
      'useForgotPasswordServiceContext must be used within a ForgotPasswordServiceContextProvider'
    );
  }
  return context;
};

export const DashboardTrackerContext = createContext<DashboardTracker | null>(
  null
);

export const useDashboardTrackerContext = () => {
  const context = useContext(DashboardTrackerContext);
  if (!context) {
    throw new Error(
      'useDashboardTrackerContext must be used within a DashboardTrackerContextProvider'
    );
  }
  return context;
};

export const SidebarTrackerContext = createContext<SidebarTracker | null>(null);

export const useSidebarTrackerContext = () => {
  const context = useContext(SidebarTrackerContext);
  if (!context) {
    throw new Error(
      'useSidebarTrackerContext must be used within a SidebarTrackerContextProvider'
    );
  }
  return context;
};

export const TimeSliderTrackerContext = createContext<TimeSliderTracker | null>(
  null
);

export const useTimeSliderTrackerContext = () => {
  const context = useContext(TimeSliderTrackerContext);
  if (!context) {
    throw new Error(
      'useTimeSliderTrackerContext must be used within a TimeSliderTrackerContextProvider'
    );
  }
  return context;
};

export const FilterTrackerContext = createContext<FilterTracker | null>(null);

export const useFilterTrackerContext = () => {
  const context = useContext(FilterTrackerContext);
  if (!context) {
    throw new Error(
      'useFilterTrackerContext must be used within a FilterTrackerContextProvider'
    );
  }
  return context;
};

export const LegacyDomainScopedStoresContext =
  createContext<LegacyDomainScopedStore | null>(null);

export const useLegacyDomainScopedStoresContext = () => {
  const context = useContext(LegacyDomainScopedStoresContext);
  if (!context) {
    throw new Error(
      'useLegacyDomainScopedStoresContext must be used within a LegacyDomainScopedStoresContext'
    );
  }
  return context;
};

export const MixpanelContext = createContext<Mixpanel | null>(null);

export const useMixpanelContext = () => {
  const context = useContext(MixpanelContext);
  if (!context) {
    throw new Error('useMixpanelContext must be used within a MixpanelContext');
  }
  return context;
};

export const ChappiServiceContext = createContext<ChappiService | null>(null);

export const useChappiServiceContext = () => {
  const context = useContext(ChappiServiceContext);
  if (!context) {
    throw new Error(
      'useChappiServiceContext must be used within a ChappiServiceContextProvider'
    );
  }
  return context;
};

export const NotificationServiceContext =
  createContext<NotificationService | null>(null);

export const useNotificationServiceContext = () => {
  const context = useContext(NotificationServiceContext);
  if (!context) {
    throw new Error(
      'useNotificationServiceContext must be used within a NotificationServiceContextProvider'
    );
  }
  return context;
};

export const IpAddressServiceContext = createContext<IpAddressService | null>(
  null
);

export const useIpAddressServiceContext = () => {
  const context = useContext(IpAddressServiceContext);
  if (!context) {
    throw new Error(
      'useIpAddressServiceContext must be used within a IpAddressServiceContextProvider'
    );
  }
  return context;
};
